"use client";

import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, Bot, Sparkles, ArrowRight, Layers, Zap, ChevronRight, CheckCircle2, Mail, User, MessageSquare, Globe, BarChart3, Shield
} from 'lucide-react';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 150]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'About', href: '#about' },
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-cyan-500/30 scroll-smooth overflow-x-hidden">
      
      {/* Navigation - High Z-Index (100) */}
      <nav className={`fixed w-full z-[100] transition-all duration-300 ${scrolled ? 'py-2 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm' : 'py-4 bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between relative z-[110]">
          
          {/* LOGO - Fixed Overlap: pointer-events-none on container prevents blocking links */}
          <div className="flex items-center pointer-events-none relative z-[120]">
             <motion.a 
               href="#home"
               initial={{ opacity: 0 }} 
               animate={{ opacity: 1 }} 
               className="pointer-events-auto block"
             >
               <img 
                 src="/heptacode-logo.png" 
                 alt="HeptaCode Technologies" 
                 className="w-[10rem] md:w-[11rem] h-auto object-contain transition-transform hover:scale-105"
               />
             </motion.a>
          </div>
          
          {/* Desktop Navigation - High Z-index relative to logo */}
          <div className="hidden md:flex items-center space-x-10 text-[12px] font-bold text-slate-800 uppercase tracking-[0.15em] relative z-[130]">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className="hover:text-[#00AEEF] transition-colors cursor-pointer">{link.name}</a>
            ))}
            <a href="#inquire" className="bg-[#001A33] text-white px-6 py-2 rounded-sm hover:bg-[#00AEEF] transition-all text-[11px]">
              INQUIRE NOW
            </a>
          </div>

          {/* Mobile Menu Toggle - Highest Z-Index */}
          <button className="md:hidden text-[#001A33] p-2 relative z-[140]" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu Overlay - Higher Padding and Stacking fix */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="absolute top-0 left-0 w-full bg-white border-b border-slate-200 pt-[10rem] pb-10 px-6 md:hidden shadow-2xl z-[90] overflow-hidden pointer-events-auto">
              <div className="flex flex-col space-y-6 relative z-[150]">
                {navLinks.map((link) => (
                  <a 
                    key={link.name} 
                    href={link.href} 
                    onClick={() => setIsMenuOpen(false)}
                    className="text-lg font-bold text-[#001A33] uppercase tracking-widest border-b border-slate-50 pb-2 active:text-[#00AEEF] cursor-pointer"
                  >
                    {link.name}
                  </a>
                ))}
                <a 
                  href="#inquire" 
                  onClick={() => setIsMenuOpen(false)}
                  className="bg-[#00AEEF] text-white py-4 rounded-sm text-center font-bold uppercase tracking-widest shadow-lg shadow-cyan-500/20"
                >
                  Inquire Now
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section - Lower Z-Index (10) */}
      <section id="home" className="relative pt-48 pb-32 px-6 min-h-[90vh] flex items-center overflow-hidden text-center flex flex-col items-center z-[10]">
        <motion.div style={{ y: y1 }} className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-white/60 z-10" />
          <img 
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=2000" 
            className="w-full h-full object-cover opacity-15 grayscale" 
            alt="Infrastructure"
          />
        </motion.div>

        <div className="max-w-7xl mx-auto relative z-20">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.h1 variants={fadeInUp} className="text-6xl md:text-8xl font-bold leading-[0.95] text-[#001A33] mb-8 tracking-tighter">
              The blueprint <br/>for <span className="text-[#00AEEF]">Scale.</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="max-w-2xl text-lg md:text-xl text-slate-500 mb-10 leading-relaxed mx-auto text-balance">
              Engineering high-precision software frameworks for market leaders. <br className="hidden md:block" /> 
              Execution-focused digital transformation.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <a href="#services" className="bg-[#001A33] text-white px-10 py-4 rounded-sm font-bold text-sm hover:bg-[#00AEEF] transition-all inline-flex items-center group">
                Explore Services <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto text-center mb-20">
            <h2 className="text-sm font-black uppercase tracking-[0.3em] text-[#00AEEF] mb-4">Our Expertise</h2>
            <p className="text-4xl font-bold text-[#001A33]">Unified Technology Solutions</p>
        </div>
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Agentic AI Systems", desc: "Autonomous AI agents designed to handle complex business logic and operational automation.", icon: <Bot />, img: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800" },
              { title: "Enterprise Engineering", desc: "Full-stack development focusing on scalable React/Node.js architectures and seamless UX.", icon: <Layers />, img: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&q=80&w=800" },
              { title: "Cloud Systems", desc: "Architecting high-availability infrastructure that ensures 99.9% uptime for mission-critical apps.", icon: <Zap />, img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=800" },
              { title: "Strategic Consulting", desc: "End-to-end technical guidance for organizations modernizing their technology stack.", icon: <Sparkles />, img: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800" },
              { title: "Architecture Audits", desc: "Software architecture reviews to identify bottlenecks and ensure long-term resilience.", icon: <BarChart3 />, img: "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?auto=format&fit=crop&q=80&w=800" },
              { title: "Digital Security", desc: "Implementing enterprise-grade security protocols across all digital product layers.", icon: <Shield />, img: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=800" }
            ].map((item, i) => (
              <motion.div 
                key={i} 
                whileHover={{ y: -10 }}
                className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden group transition-all duration-300"
              >
                <div className="h-48 overflow-hidden relative">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover grayscale-0 md:grayscale md:group-hover:grayscale-0 transition-all duration-700 ease-in-out scale-105 group-hover:scale-110"/>
                  <div className="absolute inset-0 bg-[#001A33]/10 group-hover:bg-transparent transition-colors duration-500" />
                </div>
                <div className="p-8">
                  <div className="w-12 h-12 bg-[#001A33]/5 rounded-lg flex items-center justify-center mb-6 text-[#00AEEF] group-hover:bg-[#00AEEF] group-hover:text-white transition-all">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#001A33] mb-3">{item.title}</h3>
                  <p className="text-slate-500 leading-relaxed text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
        </div>
      </section>
      
      {/* About Section */}
      <section id="about" className="py-32 px-6 bg-[#001A33] text-white text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-sm font-black uppercase tracking-[0.3em] text-[#00AEEF] mb-8">Professional Excellence</h2>
          <p className="text-2xl md:text-4xl font-light leading-tight mb-12">Expert engineers dedicated to high-precision software delivery and business intelligence.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 pt-10 border-t border-white/10">
            <div><div className="text-3xl font-bold text-[#00AEEF] mb-2">11+</div><div className="text-[9px] uppercase tracking-widest opacity-60">Years Experience</div></div>
            <div><div className="text-3xl font-bold text-[#00AEEF] mb-2">24/7</div><div className="text-[9px] uppercase tracking-widest opacity-60">System Support</div></div>
            <div className="col-span-2 md:col-span-1"><div className="text-3xl font-bold text-[#00AEEF] mb-2">100%</div><div className="text-[9px] uppercase tracking-widest opacity-60">Client Focus</div></div>
          </div>
        </div>
      </section>

      {/* Inquire Section */}
      <section id="inquire" className="py-32 px-6 bg-[#F9FAFB]">
        <div className="max-w-6xl mx-auto bg-[#001A33] rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row">
            <div className="p-12 text-white md:w-1/2 flex flex-col justify-center">
              <h2 className="text-3xl font-bold mb-4 tracking-tight">Initialize a Consultation.</h2>
              <p className="text-slate-400 mb-10 text-base">Partner with HeptaCode for your next software project.</p>
              <div className="space-y-5 text-sm">
                <div className="flex items-center space-x-3"><Mail className="text-[#00AEEF] w-4 h-4" /> <span>contact@heptacodetech.com</span></div>
                <div className="flex items-center space-x-3"><Globe className="text-[#00AEEF] w-4 h-4" /> <span>www.heptacodetech.com</span></div>
              </div>
            </div>
            <div className="bg-white p-12 md:w-1/2">
              <form className="space-y-4">
                <input type="text" placeholder="Full Name" className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:border-[#00AEEF] transition-all" />
                <input type="email" placeholder="Business Email" className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:border-[#00AEEF] transition-all" />
                <textarea rows="3" placeholder="How can we help?" className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:border-[#00AEEF] transition-all resize-none"></textarea>
                <button className="w-full bg-[#00AEEF] text-white font-bold py-4 rounded-lg hover:bg-cyan-600 transition-all shadow-md">Submit Inquiry</button>
              </form>
            </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 border-t border-slate-200 text-center">
        <img src="/heptacode-logo.png" className="w-[7rem] mx-auto mb-6 grayscale opacity-50" />
        <p className="text-slate-400 text-[9px] uppercase tracking-[0.4em] font-bold">© 2026 HEPTACODE TECHNOLOGIES. ALL RIGHTS RESERVED.</p>
      </footer>
    </div>
  );
};

export default Home;